import { Form } from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
  useStocksServiceGetInquiryEndpoint,
  useStocksServiceGetIssuanceEndpoint
} from 'src/api/queries'
import { LoadingScreen, PageBreadcrumbs } from 'src/components'
import { useFileHandler } from 'src/hooks'
import { useLocales } from 'src/locales'
import { toDayjs } from 'src/utils'

import { StockIssuanceAcceptedDetails } from './StockIssuanceAcceptedDetails'

export const StockIssuanceDetails = () => {
  const [form] = Form.useForm()

  const { t } = useLocales()

  const { id } = useParams()

  const { setExistingFiles } = useFileHandler({
    form,
    folder: `issuance-${id}`
  })

  const { data, isLoading } = useStocksServiceGetIssuanceEndpoint(
    {
      id: String(id)
    },
    [String(id)],
    {
      enabled: !!id
    }
  )

  const { data: inquire } = useStocksServiceGetInquiryEndpoint(
    {
      id: String(data?.parentDocuments?.[0]?.id)
    },
    [String(data?.parentDocuments?.[0]?.id)],
    {
      enabled: !!data
    }
  )

  useEffect(() => {
    const existingFileIds = inquire?.mediaFiles
      ?.map((file) => file?.id)
      .filter(Boolean) as string[]

    setExistingFiles(existingFileIds)

    const initialFormValues = {
      documentDate: data?.documentDate ? dayjs(data?.documentDate) : undefined,
      documentNumber: data?.documentNumber,
      takeoverDate: data?.takeoverDate ? dayjs(data?.takeoverDate) : undefined,
      supplier: {
        name: data?.supplier?.name,
        id: data?.supplier?.id
      },
      beneficiary: {
        name: data?.beneficiary?.name,
        id: data?.beneficiary?.id
      },
      docs: existingFileIds?.map((id) => ({
        uid: id,
        name: `File-${id}`,
        status: 'done'
      })),
      showTransportInfo: !!data?.transporter?.id,
      transporterId: data?.transporter?.id,
      accompanyingNotice: data?.accompanyingNotice,
      deliveryDate: data?.deliveryDate ? dayjs(data?.deliveryDate) : undefined,
      inquiryItems: inquire?.items?.map((item) => ({
        name: `${item?.internationalName?.name} ${item?.internationalName?.dosage}`,
        internationalNameId: item?.internationalName?.id,
        form: {
          name: item?.productForm?.name,
          id: item?.productForm?.id
        },
        quantity: item?.quantity,
        items: item?.beneficiaryStocks || []
      })),
      medicine: data?.items?.map((issuanceItem) => ({
        inquiryItemId: issuanceItem?.id,
        name: `${issuanceItem?.internationalName?.name} ${issuanceItem?.doses}`,
        producer: issuanceItem?.producer?.name,
        expirationDate: toDayjs(issuanceItem?.expirationDate),
        barCode: issuanceItem?.barCode,
        quantity: issuanceItem?.quantity,
        unitPrice: issuanceItem?.unitPrice,
        unit: {
          name: issuanceItem.productForm?.name,
          id: issuanceItem?.productForm?.id
        }
      }))
    }

    form.setFieldsValue(initialFormValues)
  }, [data, inquire])

  if (isLoading && !data && !inquire) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={t('stock_applications.liberation_document')}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.applications') },
        {
          title: t('stock_applications.liberation_document')
        }
      ]}
    >
      <Form form={form} layout="vertical" disabled>
        <StockIssuanceAcceptedDetails
          data={data}
          inquireData={inquire}
          form={form}
        />
      </Form>
    </PageBreadcrumbs>
  )
}
